import { Link, useNavigate, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { getActorById, getMoviesForActor } from "@/utils/data";

export default function ActorPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const actor = getActorById(id);

  useSeo({
    title: actor
      ? `${actor.name} 演员介绍及参演电影 - 蜜桃成熟时1997影视内容站`
      : "演员介绍 - 蜜桃成熟时1997影视内容站",
    description: actor
      ? `${actor.name}演员资料与简介，参演作品包括多部九十年代经典亚洲电影。查看其代表作品与相关资讯。`
      : "经典电影演员资料与参演作品介绍。",
    keywords: actor ? `${actor.name},演员介绍,${actor.name}电影,经典电影演员` : undefined,
  });

  if (!actor) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
          <i className="ri-user-unfollow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          未找到该演员资料
        </h1>
        <p className="mt-3 text-sm text-foreground-500">该演员资料可能尚未收录。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const filmography = getMoviesForActor(actor);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "演员介绍" },
            { label: actor.name },
          ]}
        />
      </div>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12">
          <div className="w-40 h-40 md:w-56 md:h-56 shrink-0 rounded-lg overflow-hidden border border-background-200 bg-background-200">
            <img
              src={actor.avatar}
              alt={`${actor.name} 演员头像`}
              title={`${actor.name} - 演员介绍`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
              <i className="ri-user-star-line"></i>
              演员资料
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {actor.name}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose max-w-3xl">
              {actor.bio}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回上一页
              </button>
              <Link
                to="/guides"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-newspaper-line"></i>
                查看相关资讯与指南
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100">
        <div className="mb-6 md:mb-8">
          <p className="text-xs font-medium tracking-widest text-primary-600 uppercase mb-2">
            Filmography
          </p>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
            主要参演电影
          </h2>
          <p className="mt-2 text-sm text-foreground-600">共 {filmography.length} 部作品，点击查看详情。</p>
        </div>
        {filmography.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filmography.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-500">该演员暂无收录作品。</p>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-information-line text-primary-600"></i>
            相关资讯提示
          </h2>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            想了解更多关于这位演员与其代表作的分析？可以前往
            <Link to="/features/plot-analysis" className="text-primary-600 hover:underline mx-1">
              剧情解析专题
            </Link>
            阅读深度文章，或在
            <Link to="/guides" className="text-primary-600 hover:underline mx-1">
              观影指南
            </Link>
            中查看影视史背景与最新资讯。
          </p>
        </div>
      </section>
    </div>
  );
}